import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding, HostListener, Inject,
  Input, Optional,
  Output
} from "@angular/core";
import {ICalendarDateParams, TCalendarCell} from "../../../shared";
import {DateControllerStateDirective} from "../../directives";

@Component({
  selector: 'int-calendar-cell',
  template: `
    <span class="c-inner">
      <button
        [disabled]="isDisabled"
        (click)="onSelectHandler()"
        class="c-action"
      >
        {{ cellData.day }}
      </button>
      <span class="current-day-mark"></span>
    </span>
  `,
  styleUrls: ['./calendar-cell.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class IntCalendarCellComponent {

  @Input() pluginState: ICalendarDateParams = {} as ICalendarDateParams;
  @Input() cellData: TCalendarCell = {} as TCalendarCell;
  @Input() selectedDates: Array<ICalendarDateParams> = [];
  @Input() minDate: Date | null = null;
  @Input() maxDate: Date | null = null;
  @Input() markWeekends: boolean = true;

  @Output() onSelect = new EventEmitter<Date>();

  enteredDate: TCalendarCell | null = null;
  currentDate: Date = new Date();

  constructor(
    @Optional()
    @Inject(DateControllerStateDirective) protected readonly controller: DateControllerStateDirective
  ) {
  }

  get isDisabled() {
    const {year, month, day} = this.cellData;
    const ts = new Date(year, month - 1, day).getTime();
    return (this.minDate !== null ? ts < this.minDate.getTime() : false) || (this.maxDate !== null
      ? ts > this.maxDate.getTime()
      : false
    )
  }

  getSelectedTimestamps() {
    const d1 = new Date(this.selectedDates[0]?.year, this.selectedDates[0]?.month, this.selectedDates[0]?.day).getTime();
    const d2 = this.selectedDates[1]
      ? new Date(this.selectedDates[1]?.year, this.selectedDates[1]?.month, this.selectedDates[1]?.day).getTime()
      : new Date(
        <number>this.controller.hoveredCellState?.year,
        <number>this.controller.hoveredCellState?.month - 1,
        this.controller.hoveredCellState?.day
      ).getTime()
    const sortDates = [d1, d2].sort();
    return sortDates;
  }

  @HostBinding('class.self-day')
  get _selfDay() {
    const {month} = this.cellData;
    const {month: _month} = this.pluginState;
    return (_month || 0) + 1 === month;
  }

  @HostBinding('class.is-weekend')
  get _isWeekend() {
    const {dayInWeek} = this.cellData;
    return this.markWeekends && ((dayInWeek === 6) || (dayInWeek === 7))
  }

  @HostBinding('class.is-selected')
  get _isSelected() {
    return this.selectedDates.some((date) => {
      const {year, month, day} = date;
      const {year: _year, month: _month, day: _day} = this.cellData;
      return (_day === day) && ((_month - 1) === month) && (_year === year);
    });
  }

  @HostBinding('class.is-disabled') get _isDisabled() {
    return this.isDisabled;
  }

  @HostBinding('class.is-inside-range')
  get _isInsideRange() {
    const {year: _year, month: _month, day: _day} = this.cellData;
    const cellTimestamp = new Date(_year, _month - 1, _day).getTime();
    const [min, max] = this.getSelectedTimestamps();
    return cellTimestamp > min && cellTimestamp < max;
  }

  @HostBinding('class.is-start-range')
  get _isStartRange() {
    const {year: _year, month: _month, day: _day} = this.cellData;
    const cellTimestamp = new Date(_year, _month - 1, _day).getTime();
    const [min] = this.getSelectedTimestamps();
    return cellTimestamp === min;
  }

  @HostBinding('class.is-end-range')
  get _isEndRange() {
    const {year: _year, month: _month, day: _day} = this.cellData;
    const cellTimestamp = new Date(_year, _month - 1, _day).getTime();
    const [_, max] = this.getSelectedTimestamps();
    return cellTimestamp === max;
  }

  @HostBinding('class.is-current-date')
  get _isCurrentDate() {
    const {year: _year, month: _month, day: _day} = this.cellData;
    const cY = this.currentDate.getFullYear();
    const cM = this.currentDate.getMonth();
    const cD = this.currentDate.getDate();
    return (_year === cY) && (_month - 1 === cM) && (_day === cD);
  }

  @HostBinding('attr.data-calendar-type') get _type() {
    return this.controller.type;
  }

  @HostListener('mouseenter')
  mouseinter() {
    if(this.controller.isSingle) {
      return;
    }
    this.controller.hoveredDate(this.cellData);
  }

  onSelectHandler() {
    const {day: day, month, year} = this.cellData;
    const selectedDate = new Date( year, month - 1, day );
    this.onSelect.emit(selectedDate);
  }
}
